import React from "react"
import { graphql } from "gatsby"
import { PortableText } from "@portabletext/react"
import SanityImage from "gatsby-plugin-sanity-image"
import useBreakpoints from "hooks/useBreakpoints"
import theme from "styles/theme"

const expand = styles =>
  theme.expandQueries(styles, ["root", theme.mobile])

const translateContentAlignY = contentAlignY => {
    const translations = {
        top: "start",
        middle: "center",
    }
    return translations[contentAlignY.toLowerCase()] || "start"
}

const headingStyle = expand({
    fontSize: [15, 12],
    fontWeight: 700,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    color: theme.midBlue,
})

const textStyle = expand({
    fontSize: [14, 11],
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: "-0.02667em",
    color: theme.textGray,
})

const cellPadding = expand({
    padding: [20, 15],
})

const tableColumnStyle = {
    display: "flex",
    backgroundColor: theme.white,
    borderRight: `2px solid ${theme.midBlue}`,
    "> *": {
        margin: 0,
        ":not(:last-of-type)": {
            marginBottom: "1em",
        }
    },
}

const tableColumnStyleHeaderMod = {
    justifyContent: "center",
    backgroundColor: theme.lightBlue30,
}

const ComplexTableBlock = ({ header, colsWidth, rows, footnotes }) => {
    const { desktop: isDesktopAndUp } = useBreakpoints()
    const totalCols = Math.max(...rows.map(({columns = []}) => columns.length))
    const stacks = Array.from(Array(totalCols).keys()).map(() => Array.from(Array(rows.length).values()))
    rows.forEach(({columns}, rowIndex) => {
        columns.map(column => column.colspan > 1
            ? Array.from(Array(column.colspan).keys()).map(() => column)
            : column )
            .flat()
            .forEach((column, columnIndex) => {
                if (typeof stacks[columnIndex][rowIndex] === "undefined") {
                    stacks[columnIndex][rowIndex] = column
                }
            })
    })
    return (
        <BlockWrapper
            header={header}
            footnotes={footnotes}
        >
            {!isDesktopAndUp
                ? stacks.map((stackRows, stackIndex) =>
                    <Stack
                        key={`complext-table-stack-${stackIndex}`}
                        stackRows={stackRows}
                    />
                )
                : rows.map(({columns}, rowIndex) => {
                    let colspanCount = 1
                    const isHeader = rowIndex < 1
                    return (
                        <TableRow
                            key={`complex-table-row-${rowIndex}`}
                            totalCols={totalCols}
                            colsWidth={colsWidth}
                        >
                            {columns.map((column, columnIndex) => {
                                const startCol = colspanCount
                                const endCol = startCol + column.colspan
                                colspanCount = endCol <= totalCols ? endCol : 1
                                return (
                                    <TableColumn
                                        key={`complex-table-col-${columnIndex}`}
                                        isHeader={isHeader}
                                        startCol={startCol}
                                        endCol={endCol}
                                        {...column}
                                    />
                                )
                            })}
                        </TableRow>
                    )
                })
            }
        </BlockWrapper>
    )
}

export default ComplexTableBlock

const BlockWrapper = ({
    header,
    children,
    footnotes,
    ...props
}) => {
    return (
        <section
            className="mro-infotable"
            css={expand({
                maxWidth: "960px",
                margin: "3.529em auto",
                borderTop: `2px solid ${theme.midBlue}`,
            })}
            {...props}
        >
            {Array.isArray(header) && header.length &&
                <TitleArea value={header} />
            }
            {children}
            <div
                className="mro-infotable__footnotes"
                css={{
                    borderTop: `1px solid ${theme.softGray}`,
                    paddingTop: "1em",
                    marginTop: 60,
                }}
            >
                {footnotes.map((footnote, index) => (
                    <p
                        className="mro-infotable__footnote"
                        css={expand({
                            margin: ".25em",
                            fontSize: [12, 11],
                            fontStyle: "italic",
                            lineHeight: 1.6,
                            color: theme.gray,
                        })}
                    >
                        <sup>{++index}</sup> {footnote}
                    </p>
                ))}
            </div>
        </section>
    )
}

const TableRow = ({
    totalCols,
    colsWidth = [],
    ...props
}) => {
    const gridTemplateColumns = Array.from(Array(totalCols).keys()).map(index => {
        const { widthPercent, minWidthPixel } = colsWidth[index] || {}
        const colWidth = widthPercent ? widthPercent + "%" : "1fr"
        const colMinWidth = minWidthPixel ? minWidthPixel + "px" : ""
        return colMinWidth ? `minmax(${colMinWidth}, ${colWidth})` : colWidth
    }).join(" ")
    return (
        <ul
            className="mro-infotable__row"
            css={expand({
                display: "grid",
                gridTemplateColumns,
                listStyle: "none",
                'html body &': {
                    margin: 0,
                    padding: 0,
                },
                borderBottom: `2px solid ${theme.midBlue}`,
                borderLeft: `2px solid ${theme.midBlue}`,
            })}
            {...props}
        />
    )
}

const TableColumn = ({
    isHeader,
    startCol,
    endCol,
    contentAlignY,
    content,
    ...props
}) => {
    return (
        <li
            className="mro-infotable__col"
            css={{
                ...tableColumnStyle,
                ...cellPadding,
                ...(isHeader ? headingStyle : textStyle),
                ...(isHeader ? tableColumnStyleHeaderMod : {}),
                'html body &': {
                    margin: 0,
                },
                alignItems: translateContentAlignY(contentAlignY),
                gridColumn: `${startCol} / ${endCol}`,
            }}
            {...props}
        >
            <ColumnContent value={content} />
        </li>
    )
}

const Stack = ({
    stackRows,
    ...props
}) => {
    return (
        <ul
            className="mro-infotable__col"
            css={expand({
                display: "block",
                listStyle: "none",
                'html body &': {
                    margin: 0,
                    padding: 0,
                },
            })}
            {...props}
        >
            {stackRows.map((stackRow, index) => {
                const isHeader = index < 1
                const {
                    contentAlignY,
                    content,
                } = stackRow
                return(
                    <li
                        key={`complext-table-stack-row-${index}`}
                        className="mro-infotable__row"
                        css={{
                            ...tableColumnStyle,
                            ...cellPadding,
                            ...(isHeader ? headingStyle : textStyle),
                            ...(isHeader ? tableColumnStyleHeaderMod : {}),
                            'html body &': {
                                margin: 0,
                            },
                            alignItems: translateContentAlignY(contentAlignY),
                            borderLeft: `2px solid ${theme.midBlue}`,
                            borderRight: `2px solid ${theme.midBlue}`,
                            borderBottom: `2px solid ${theme.midBlue}`,
                        }}
                        {...props}
                    >
                        <ColumnContent value={content} />
                    </li>
                )
            })}
        </ul>
    )
}

const ColumnContent = (props) => {
    return (
        <PortableText
            {...props}
            components={{
                ...props.components || {},
                marks: {
                    ...props.components?.marks || {},
                    sup: props => <sup>{props.children}</sup>,
                    sub: props => <sub>{props.children}</sub>,
                },
                list: {
                    bullet: props => (
                        <ul
                            css={{
                                listStyleType: "disc",
                                // listStylePosition: "inside",
                                padding: 0,
                                paddingLeft: "1ch",
                            }}
                        >
                            {props.children}
                        </ul>
                    ),
                },
                listItem: {
                    bullet: props => (
                        <li
                            style={{
                                paddingLeft: ".8ch",
                            }}
                        >
                            {props.children}
                        </li>
                    ),
                },
                types: {
                    ...props.components?.types || {},
                    complexTableBlockInlineIcon: props => { console.log(props); return (
                        <SanityImage
                            {...props.value.icon}
                            width={42}
                            css={expand({
                                float: "left",
                                maxWidth: 42,
                                marginRight: props.value?.marginRight || 0,
                            })}
                        />
                    )},
                },
            }}
        />
    )
}

const TitleArea = (props) => {
    return (
        <header
            className="mro-infotable__title"
            css={expand({
                textAlign: "center",
                borderBottom: `2px solid ${theme.midBlue}`,
                borderLeft: `2px solid ${theme.midBlue}`,
                borderRight: `2px solid ${theme.midBlue}`,
                ...cellPadding,
                ...textStyle,
                p: {
                    margin: 0,
                }
            })}
        >
            <PortableText
                {...props}
                components={{
                    ...props.components || {},
                    marks: {
                        ...props.components?.marks || {},
                        sup: props => (
                            <sup>{props.children}</sup>
                        ),
                    },
                    block: {
                        ...props.components?.block || {},
                        heading: props => (
                            <h4
                                css={headingStyle}
                            >
                                {props.children}
                            </h4>
                        ),
                    }
                }}
            />
        </header>
    )
}

export const query = graphql`
  fragment ComplexTableBlock on SanityComplexTableBlock {
    header: _rawHeader(resolveReferences: { maxDepth: 4 })
    colsWidth {
        columnNum
        widthPercent
        minWidthPixel
    }
    rows {
        columns {
            colspan
            contentAlignY
            content: _rawContent(resolveReferences: { maxDepth: 10 })
        }
    }
    footnotes
  }
`
